import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils';
import { motion } from 'framer-motion';
import { Wrench, Gauge, Droplets, CircleDot, Thermometer, Settings, ArrowRight } from 'lucide-react';

const services = [
  {
    icon: Gauge,
    title: 'Engine Diagnostics',
    description: 'Advanced computer diagnostics to identify and resolve engine issues quickly.',
  },
  {
    icon: CircleDot,
    title: 'Brake Services',
    description: 'Complete brake inspection, repair, and replacement for your safety.',
  },
  {
    icon: Droplets,
    title: 'Oil Changes',
    description: 'Premium synthetic and conventional oil changes with multi-point inspection.',
  },
  {
    icon: Settings,
    title: 'Tire Services',
    description: 'Rotation, balancing, alignment, and new tire installation services.',
  },
  {
    icon: Thermometer,
    title: 'AC & Heating',
    description: 'Climate control system diagnostics, repair, and recharge services.',
  },
  {
    icon: Wrench,
    title: 'General Repairs',
    description: 'Comprehensive repair services for all makes and models.',
  },
];

export default function ServicesPreview() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Complete Auto Care Solutions
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From routine maintenance to complex repairs, we've got your vehicle covered with our comprehensive range of services.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group p-8 bg-slate-50 rounded-2xl hover:bg-slate-900 transition-all duration-500 cursor-pointer"
            >
              <div className="w-14 h-14 bg-amber-100 group-hover:bg-amber-500 rounded-xl flex items-center justify-center mb-6 transition-all duration-500">
                <service.icon className="w-7 h-7 text-amber-600 group-hover:text-slate-900 transition-colors duration-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 group-hover:text-white mb-3 transition-colors duration-500">
                {service.title}
              </h3>
              <p className="text-slate-600 group-hover:text-slate-400 transition-colors duration-500">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to={createPageUrl('Services')}
            className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:text-amber-700 transition-colors group"
          >
            View All Services
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}